import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft, Save, RotateCcw, Settings, Users, Star
} from 'lucide-react';
import toast from 'react-hot-toast';
import smartScheduleService from '../services/smartScheduleService';

const WaitingListScoreConfig = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState({
    potential_scores: { ALTO: 100, MEDIO: 50, BAIXO: 10 },
    origin_bonus: { INDICACAO: 30, ANTIGO: 20, NOVO: 0 }
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Buscar configuração atual
  const { data, isLoading } = useQuery({
    queryKey: ['waiting-list-score-config'],
    queryFn: () => smartScheduleService.getWaitingListScoreConfig(),
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    networkMode: 'always'
  });

  // Forçar atualização quando dados mudarem
  useEffect(() => {
    if (data?.data) {
      console.log('[useEffect] dados recebidos, atualizando estado:', data.data);
      const newConfigs = {};
      for (const [key, val] of Object.entries(data.data)) {
        if (val && val.value) {
          console.log(`[useEffect] processando ${key}:`, val.value);
          newConfigs[key] = val.value;
        }
      }
      console.log('[useEffect] setando configs:', newConfigs);
      setConfigs(newConfigs);
      setRefreshKey(k => k + 1); // Forçar re-render
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: ({ config_key, config_value }) =>
      smartScheduleService.updateWaitingListScoreConfig({ config_key, config_value }),
    onSuccess: (data) => {
      console.log('[mutation] onSuccess - dados retornados:', data);
      toast.success('Configuração salva com sucesso!');
      // Usar os dados retornados pelo backend diretamente
      if (data.data) {
        console.log('[mutation] atualizando com:', data.data);
        setConfigs(prev => {
          const newConfigs = {
            ...prev,
            [data.data.config_key]: data.data.config_value
          };
          console.log('[mutation] novo estado:', newConfigs);
          return newConfigs;
        });
        setRefreshKey(k => k + 1); // Forçar re-render
      }
      // Invalidar cache para próximas buscas
      queryClient.invalidateQueries({ queryKey: ['waiting-list-score-config'] });
      queryClient.invalidateQueries({ queryKey: ['waiting-list'] });
      setHasChanges(false);
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao salvar configuração');
    }
  });

  const handleChange = (category, key, value) => {
    setConfigs(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: parseInt(value) || 0
      }
    }));
    setHasChanges(true);
  };

  const handleSave = (category) => {
    updateMutation.mutate({
      config_key: category,
      config_value: configs[category]
    });
  };

  const handleReset = () => {
    setConfigs({
      potential_scores: { ALTO: 100, MEDIO: 50, BAIXO: 10 },
      origin_bonus: { INDICACAO: 30, ANTIGO: 20, NOVO: 0 }
    });
    setHasChanges(true);
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div key={refreshKey} className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Link
          to="/config/fila-espera"
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          title="Voltar para a Fila de Espera"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex items-center gap-3">
          <Settings className="w-8 h-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Configuração de Scores</h1>
            <p className="text-gray-600">Defina os pontos para priorização da Lista de Espera</p>
          </div>
        </div>
      </div>

      {/* Cards de Configuração */}
      <div className="space-y-6">
        {/* Scores de Potencial */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Star className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">Scores de Potencial</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Pontuação atribuída a cada nível de potencial do cliente
          </p>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(configs.potential_scores).map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {key === 'ALTO' ? 'Alto' : key === 'MEDIO' ? 'Médio' : 'Baixo'}
                </label>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleChange('potential_scores', key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min="0"
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handleSave('potential_scores')}
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* Bônus de Origem */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Bônus de Origem</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            Pontos adicionais conforme a origem do cliente (indicação, cliente antigo, etc.)
          </p>
          <div className="grid grid-cols-3 gap-4">
            {Object.entries(configs.origin_bonus).map(([key, value]) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {key === 'INDICACAO' ? 'Indicação' : key === 'ANTIGO' ? 'Antigo' : 'Novo'}
                </label>
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleChange('origin_bonus', key, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  min="0"
                />
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => handleSave('origin_bonus')}
              disabled={updateMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>

        {/* Resumo e Reset */}
        <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600">
              Os scores são usados para ordenar automaticamente a Lista de Espera.
              Clientes com maior pontuação aparecem primeiro.
            </p>
          </div>
          <button
            onClick={handleReset}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
          >
            <RotateCcw className="w-4 h-4" />
            Restaurar Padrões
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitingListScoreConfig;
